import { TaxonomyView } from "./taxonomyView";
import type { CorpusFeedComposition } from "@shared/types/corpus/api.ts";

/** One health field of a feed's composition. */
export interface CompositionField {
  urn: string;
  /** The field's name; its URN when the taxonomy no longer knows it. */
  name: string;
  papers: number;
  papersText: string;
}

/** One subject of a feed's composition. */
export interface CompositionSubject {
  subject: string;
  /** The subject's name; its id when none is known. */
  name: string;
  papers: number;
  papersText: string;
}

/** Reads what a corpus feed's papers are made of the way a person sees it. */
export class CorpusFeedCompositionView {
  /** `taxonomy` is the subject skeleton the field names are read from. */
  constructor(
    readonly composition: CorpusFeedComposition,
    readonly taxonomy: TaxonomyView = new TaxonomyView([])
  ) {}

  /** The health fields, the one with the most papers first. */
  get fields(): CompositionField[] {
    return Object.entries(this.composition.byField)
      .map(([urn, papers]) => ({
        urn,
        name: this.taxonomy.findField(urn)?.enName ?? urn,
        papers,
        papersText: papers.toLocaleString(),
      }))
      .sort((a, b) => b.papers - a.papers || a.name.localeCompare(b.name));
  }

  /** The subjects, the one with the most papers first; the ids a name is looked up for. */
  get subjectIds(): string[] {
    return this.subjects({}).map((subject) => subject.subject);
  }

  /** The subjects named from `names`, the one with the most papers first. A subject with no name
   *  is shown by its id. */
  subjects(names: Record<string, string>): CompositionSubject[] {
    return Object.entries(this.composition.bySubject)
      .map(([subject, papers]) => ({
        subject,
        name: names[subject] ?? subject,
        papers,
        papersText: papers.toLocaleString(),
      }))
      .sort((a, b) => b.papers - a.papers || a.name.localeCompare(b.name));
  }

  /** No paper of the feed has a health field. */
  get isEmpty(): boolean {
    return Object.keys(this.composition.byField).length === 0;
  }

  /** No paper of the feed has a subject. */
  get hasNoSubjects(): boolean {
    return Object.keys(this.composition.bySubject).length === 0;
  }
}
